import React, { useState, useRef, useCallback } from 'react';

interface InvoiceModalProps {
    isOpen: boolean;
    onClose: () => void;
    companyName: string;
    companyTaxId: string;
}

interface InvoiceItem {
    id: string;
    description: string;
    quantity: string;
    unitPrice: string;
}

const generateInvoiceNumber = () => {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const l1 = letters[Math.floor(Math.random() * 26)];
    const l2 = letters[Math.floor(Math.random() * 26)];
    const num = String(Math.floor(Math.random() * 100000000)).padStart(8, '0');
    return `${l1}${l2}-${num}`;
};

const formatDate = (date: Date) => {
    const rocYear = date.getFullYear() - 1911;
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${rocYear}年${month}月${day}日`;
};

export const InvoiceModal: React.FC<InvoiceModalProps> = ({
    isOpen,
    onClose,
    companyName,
    companyTaxId,
}) => {
    const today = new Date();

    // 賣方資訊
    const [sellerName, setSellerName] = useState('');
    const [sellerTaxId, setSellerTaxId] = useState('');
    const [sellerAddress, setSellerAddress] = useState('');
    const [invoiceDate, setInvoiceDate] = useState(
        `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
    );
    const [invoiceNumber] = useState(generateInvoiceNumber());

    // 買受人資訊（預填公司資訊）
    const [buyerName, setBuyerName] = useState(companyName);
    const [buyerTaxId, setBuyerTaxId] = useState(companyTaxId);
    const [buyerAddress, setBuyerAddress] = useState('');

    // 發票明細
    const [items, setItems] = useState<InvoiceItem[]>([
        { id: '1', description: '', quantity: '1', unitPrice: '' },
    ]);

    // 稅率設定
    const [taxType, setTaxType] = useState<'taxed' | 'zero' | 'exempt'>('taxed');
    const TAX_RATE = 0.05;

    const printRef = useRef<HTMLDivElement>(null);

    const addItem = () => {
        setItems((prev) => [
            ...prev,
            { id: String(Date.now()), description: '', quantity: '1', unitPrice: '' },
        ]);
    };

    const removeItem = (id: string) => {
        setItems((prev) => prev.filter((item) => item.id !== id));
    };

    const updateItem = (id: string, field: keyof InvoiceItem, value: string) => {
        setItems((prev) =>
            prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
        );
    };

    const parseNum = (v: string) => parseFloat(v) || 0;

    const subtotalBeforeTax = items.reduce(
        (sum, item) => sum + parseNum(item.quantity) * parseNum(item.unitPrice),
        0
    );

    const taxAmount = taxType === 'taxed' ? Math.round(subtotalBeforeTax * TAX_RATE) : 0;
    const totalAmount = subtotalBeforeTax + taxAmount;

    const formatCurrency = (n: number) =>
        n.toLocaleString('zh-TW', { minimumFractionDigits: 0 });

    const handlePrint = useCallback(() => {
        const content = printRef.current;
        if (!content) return;
        const printWindow = window.open('', '_blank', 'width=900,height=700');
        if (!printWindow) return;
        printWindow.document.write(`
      <html>
        <head>
          <title>統一發票 ${invoiceNumber}</title>
          <meta charset="utf-8" />
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+TC:wght@400;500;700;900&display=swap');
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: 'Noto Sans TC', sans-serif; background: #fff; color: #000; }
            .invoice-print { padding: 24px; max-width: 800px; margin: 0 auto; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #000; padding: 6px 10px; font-size: 13px; }
            th { background: #f0f0f0; font-weight: 700; text-align: center; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .header-box { border: 2px solid #000; padding: 12px; margin-bottom: 12px; }
            .section-title { font-size: 11px; color: #555; font-weight: 500; }
            .section-value { font-size: 14px; font-weight: 700; }
            .total-row { font-weight: 700; background: #f8f8f8; }
            .tax-label { font-size: 11px; background: #e8f4e8; }
          </style>
        </head>
        <body>
          ${content.innerHTML}
        </body>
      </html>
    `);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => printWindow.print(), 300);
    }, [invoiceNumber]);

    if (!isOpen) return null;

    const parsedDate = new Date(invoiceDate);

    return (
        <div
            className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto"
            style={{ paddingTop: '20px', paddingBottom: '20px' }}
        >
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal Container */}
            <div
                className="relative w-full max-w-3xl mx-4 rounded-2xl shadow-2xl overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #0f2442 100%)' }}
            >
                {/* Modal Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
                    <div className="flex items-center gap-3">
                        <div
                            className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ background: 'rgba(255,255,255,0.15)' }}
                        >
                            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <div>
                            <h2 className="text-white font-bold text-lg">統一發票</h2>
                            <p className="text-blue-200 text-xs">Uniform Invoice</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Modal Body */}
                <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 120px)' }}>

                    {/* ── 稅別切換 ── */}
                    <div className="flex gap-2 mb-5">
                        {([
                            { value: 'taxed', label: '應稅（含稅）' },
                            { value: 'zero', label: '零稅率' },
                            { value: 'exempt', label: '免稅' },
                        ] as const).map((opt) => (
                            <button
                                key={opt.value}
                                onClick={() => setTaxType(opt.value)}
                                className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all duration-200 ${taxType === opt.value
                                        ? 'bg-amber-400 text-gray-900 shadow-lg scale-105'
                                        : 'bg-white/10 text-white/70 hover:bg-white/20'
                                    }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>

                    {/* ── 發票本體（可列印區） ── */}
                    <div
                        ref={printRef}
                        className="bg-white rounded-xl overflow-hidden text-gray-900"
                        style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                    >
                        {/* 發票頂部裝飾條 */}
                        <div className="h-2" style={{ background: 'linear-gradient(90deg, #1e3a5f, #0ea5e9, #22c55e, #f59e0b)' }} />

                        {/* 發票主體 */}
                        <div className="p-6">
                            {/* 標題區 */}
                            <div className="text-center mb-4 border-b-2 border-gray-200 pb-4">
                                <div className="text-xs text-gray-500 mb-1">
                                    {taxType === 'taxed' && '三聯式'}
                                    {taxType === 'zero' && '三聯式（零稅率）'}
                                    {taxType === 'exempt' && '三聯式（免稅）'}
                                    統一發票
                                </div>
                                <h1 className="text-2xl font-black tracking-widest text-gray-900">統　一　發　票</h1>
                                <div className="text-xs text-gray-500 mt-1">UNIFORM INVOICE</div>
                            </div>

                            {/* 發票資訊列 */}
                            <div className="grid grid-cols-3 gap-3 mb-4 text-xs">
                                <div className="border border-gray-300 rounded p-2">
                                    <div className="text-gray-500 mb-1 font-medium">發票號碼</div>
                                    <div className="font-black text-base tracking-wider text-blue-800">{invoiceNumber}</div>
                                </div>
                                <div className="border border-gray-300 rounded p-2">
                                    <div className="text-gray-500 mb-1 font-medium">開立日期</div>
                                    <div className="font-bold text-sm">
                                        {isNaN(parsedDate.getTime()) ? '-' : formatDate(parsedDate)}
                                    </div>
                                </div>
                                <div className="border border-gray-300 rounded p-2">
                                    <div className="text-gray-500 mb-1 font-medium">稅別</div>
                                    <div className="font-bold text-sm">
                                        {taxType === 'taxed' && '應稅（含稅）'}
                                        {taxType === 'zero' && '零稅率'}
                                        {taxType === 'exempt' && '免稅'}
                                    </div>
                                </div>
                            </div>

                            {/* 賣方 & 買受人 */}
                            <div className="grid grid-cols-2 gap-3 mb-4">
                                {/* 賣方 */}
                                <div className="border border-gray-300 rounded p-3">
                                    <div className="font-bold text-xs text-gray-600 mb-2 border-b border-gray-200 pb-1">賣　方</div>
                                    <div className="space-y-2 text-xs">
                                        <div>
                                            <label className="text-gray-500 block mb-0.5">營業人名稱</label>
                                            <input
                                                type="text"
                                                value={sellerName}
                                                onChange={(e) => setSellerName(e.target.value)}
                                                placeholder="請輸入賣方名稱"
                                                className="w-full border-b border-gray-300 focus:border-blue-500 outline-none text-sm font-medium py-0.5 bg-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-gray-500 block mb-0.5">統一編號</label>
                                            <input
                                                type="text"
                                                value={sellerTaxId}
                                                onChange={(e) => setSellerTaxId(e.target.value)}
                                                placeholder="00000000"
                                                maxLength={8}
                                                className="w-full border-b border-gray-300 focus:border-blue-500 outline-none text-sm font-medium py-0.5 bg-transparent tracking-widest"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-gray-500 block mb-0.5">地址</label>
                                            <input
                                                type="text"
                                                value={sellerAddress}
                                                onChange={(e) => setSellerAddress(e.target.value)}
                                                placeholder="請輸入賣方地址"
                                                className="w-full border-b border-gray-300 focus:border-blue-500 outline-none text-sm font-medium py-0.5 bg-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-gray-500 block mb-0.5">開立日期</label>
                                            <input
                                                type="date"
                                                value={invoiceDate}
                                                onChange={(e) => setInvoiceDate(e.target.value)}
                                                className="w-full border-b border-gray-300 focus:border-blue-500 outline-none text-sm font-medium py-0.5 bg-transparent"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* 買受人 */}
                                <div className="border-2 border-blue-400 rounded p-3">
                                    <div className="font-bold text-xs text-blue-700 mb-2 border-b border-blue-200 pb-1">買　受　人</div>
                                    <div className="space-y-2 text-xs">
                                        <div>
                                            <label className="text-gray-500 block mb-0.5">營業人名稱</label>
                                            <input
                                                type="text"
                                                value={buyerName}
                                                onChange={(e) => setBuyerName(e.target.value)}
                                                placeholder="請輸入買受人名稱"
                                                className="w-full border-b border-blue-300 focus:border-blue-600 outline-none text-sm font-bold py-0.5 bg-transparent text-blue-900"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-gray-500 block mb-0.5">統一編號</label>
                                            <input
                                                type="text"
                                                value={buyerTaxId}
                                                onChange={(e) => setBuyerTaxId(e.target.value)}
                                                placeholder="00000000"
                                                maxLength={8}
                                                className="w-full border-b border-blue-300 focus:border-blue-600 outline-none text-sm font-bold py-0.5 bg-transparent text-blue-900 tracking-widest"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-gray-500 block mb-0.5">地址</label>
                                            <input
                                                type="text"
                                                value={buyerAddress}
                                                onChange={(e) => setBuyerAddress(e.target.value)}
                                                placeholder="請輸入買受人地址"
                                                className="w-full border-b border-blue-300 focus:border-blue-600 outline-none text-sm font-bold py-0.5 bg-transparent text-blue-900"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 品項表格 */}
                            <div className="mb-4">
                                <table className="w-full text-xs border-collapse">
                                    <thead>
                                        <tr className="bg-gray-100">
                                            <th className="border border-gray-300 px-2 py-2 text-left font-bold w-8">#</th>
                                            <th className="border border-gray-300 px-2 py-2 text-left font-bold">品名（科目）</th>
                                            <th className="border border-gray-300 px-2 py-2 text-center font-bold w-16">數量</th>
                                            <th className="border border-gray-300 px-2 py-2 text-right font-bold w-24">單價</th>
                                            <th className="border border-gray-300 px-2 py-2 text-right font-bold w-24">金額</th>
                                            <th className="border border-gray-300 px-2 py-2 w-8 print:hidden"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map((item, idx) => {
                                            const lineTotal = parseNum(item.quantity) * parseNum(item.unitPrice);
                                            return (
                                                <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                                                    <td className="border border-gray-300 px-2 py-1.5 text-gray-500 text-center">{idx + 1}</td>
                                                    <td className="border border-gray-300 px-1 py-1">
                                                        <input
                                                            type="text"
                                                            value={item.description}
                                                            onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                                                            placeholder="輸入品名或科目"
                                                            className="w-full outline-none bg-transparent text-sm py-0.5 px-1 focus:bg-blue-50 rounded"
                                                        />
                                                    </td>
                                                    <td className="border border-gray-300 px-1 py-1">
                                                        <input
                                                            type="number"
                                                            value={item.quantity}
                                                            onChange={(e) => updateItem(item.id, 'quantity', e.target.value)}
                                                            min="1"
                                                            className="w-full outline-none bg-transparent text-sm text-center py-0.5 focus:bg-blue-50 rounded"
                                                        />
                                                    </td>
                                                    <td className="border border-gray-300 px-1 py-1">
                                                        <input
                                                            type="number"
                                                            value={item.unitPrice}
                                                            onChange={(e) => updateItem(item.id, 'unitPrice', e.target.value)}
                                                            placeholder="0"
                                                            min="0"
                                                            className="w-full outline-none bg-transparent text-sm text-right py-0.5 focus:bg-blue-50 rounded"
                                                        />
                                                    </td>
                                                    <td className="border border-gray-300 px-2 py-1.5 text-right font-medium">
                                                        {lineTotal > 0 ? formatCurrency(lineTotal) : '-'}
                                                    </td>
                                                    <td className="border border-gray-300 px-1 py-1 text-center print:hidden">
                                                        {items.length > 1 && (
                                                            <button
                                                                onClick={() => removeItem(item.id)}
                                                                className="w-5 h-5 rounded-full bg-red-100 text-red-500 hover:bg-red-200 flex items-center justify-center text-xs font-bold mx-auto transition-colors"
                                                            >
                                                                ×
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                {/* 新增品項按鈕 */}
                                <button
                                    onClick={addItem}
                                    className="mt-2 text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 transition-colors print:hidden"
                                >
                                    <span className="w-4 h-4 rounded-full border border-blue-600 flex items-center justify-center text-xs font-bold">+</span>
                                    新增品項
                                </button>
                            </div>

                            {/* 金額合計區 */}
                            <div className="border border-gray-300 rounded overflow-hidden mb-4">
                                <table className="w-full text-sm">
                                    <tbody>
                                        <tr className="border-b border-gray-200">
                                            <td className="px-4 py-2 text-gray-600">
                                                {taxType === 'taxed' ? '銷售額合計（未稅）' : '銷售額合計'}
                                            </td>
                                            <td className="px-4 py-2 text-right font-semibold">
                                                NT$ {formatCurrency(subtotalBeforeTax)}
                                            </td>
                                        </tr>
                                        {taxType === 'taxed' && (
                                            <tr className="border-b border-gray-200 bg-green-50">
                                                <td className="px-4 py-2 text-gray-600">
                                                    營業稅額（5%）
                                                </td>
                                                <td className="px-4 py-2 text-right font-semibold text-green-700">
                                                    NT$ {formatCurrency(taxAmount)}
                                                </td>
                                            </tr>
                                        )}
                                        {taxType === 'zero' && (
                                            <tr className="border-b border-gray-200 bg-gray-50">
                                                <td className="px-4 py-2 text-gray-600">稅額（零稅率）</td>
                                                <td className="px-4 py-2 text-right font-semibold text-gray-500">NT$ 0</td>
                                            </tr>
                                        )}
                                        {taxType === 'exempt' && (
                                            <tr className="border-b border-gray-200 bg-gray-50">
                                                <td className="px-4 py-2 text-gray-600">稅額（免稅）</td>
                                                <td className="px-4 py-2 text-right font-semibold text-gray-500">NT$ 0</td>
                                            </tr>
                                        )}
                                        <tr className="bg-blue-900 text-white">
                                            <td className="px-4 py-3 font-black text-base">總計金額</td>
                                            <td className="px-4 py-3 text-right font-black text-xl">
                                                NT$ {formatCurrency(totalAmount)}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* 含稅說明 */}
                            {taxType === 'taxed' && (
                                <div className="text-xs text-gray-500 bg-gray-50 rounded p-2 mb-4 border border-gray-200">
                                    ※ 本發票為應稅含稅發票，銷售額 NT$ {formatCurrency(subtotalBeforeTax)} + 營業稅 NT$ {formatCurrency(taxAmount)} = 含稅總計 NT$ {formatCurrency(totalAmount)}
                                </div>
                            )}

                            {/* 備注欄 / 簽章區 */}
                            <div className="grid grid-cols-3 gap-3 text-xs text-center mt-4 pt-4 border-t border-gray-200">
                                <div className="border border-gray-300 rounded p-3 h-16 flex items-end justify-center">
                                    <span className="text-gray-500">買受人簽章</span>
                                </div>
                                <div className="border border-gray-300 rounded p-3 h-16 flex items-end justify-center">
                                    <span className="text-gray-500">賣方負責人</span>
                                </div>
                                <div className="border border-gray-300 rounded p-3 h-16 flex items-end justify-center">
                                    <span className="text-gray-500">記帳員</span>
                                </div>
                            </div>

                            {/* 底部說明 */}
                            <div className="text-center text-xs text-gray-400 mt-4">
                                依統一發票使用辦法開立　本發票由電子系統開立
                            </div>
                        </div>
                    </div>

                    {/* 操作按鈕 */}
                    <div className="flex gap-3 mt-5">
                        <button
                            onClick={onClose}
                            className="flex-1 py-2.5 rounded-xl font-semibold text-white/70 border border-white/20 hover:bg-white/10 transition-colors"
                        >
                            關閉
                        </button>
                        <button
                            onClick={handlePrint}
                            className="flex-1 py-2.5 rounded-xl font-semibold text-gray-900 transition-all duration-200 flex items-center justify-center gap-2"
                            style={{ background: 'linear-gradient(135deg, #f59e0b, #f97316)' }}
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                            </svg>
                            列印發票
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
